import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Edit-mode toggle that lets a spinner reach the screen first.
 *
 * Turning every cell into an input is a heavy render, and React 17 has no
 * `useTransition` to yield for us. So the flag flips only after two animation
 * frames - by then the overlay has painted, and the freeze that follows happens
 * with a spinner on screen instead of a page that looks dead.
 */
export const useEditMode = () => {
  const [editing, setEditing] = useState(false);
  const [switching, setSwitching] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const toggle = useCallback(() => {
    setSwitching(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!mounted.current) return;
        // Batched into one render: the expensive one, after which the
        // overlay is already gone.
        setEditing((current) => !current);
        setSwitching(false);
      });
    });
  }, []);

  const reset = useCallback(() => {
    setEditing(false);
    setSwitching(false);
  }, []);

  return { editing, switching, toggle, reset };
};

export default useEditMode;
